import type { HostBooking } from '@bb/shared';
import { formatMoney, type Locale, pickLocale } from '@bb/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import {
  bookingSettingsQuery,
  hostBookingQuery,
  useCancelHostBooking,
  useMarkNoShow,
  useReassignBooking,
  useRecreateCalendarEvent,
  useRefundBooking,
  useRescheduleHostBooking,
  useResendConfirmation,
  useSetHostNotes,
  useUnmarkNoShow,
} from '@/features/booking/api';
import { SlotPicker } from '@/features/public-booking/slot-picker';
import { useMembers } from '@/features/workspace/api';
import { ApiRequestError } from '@/lib/api';
import { btnPrimary, btnSecondary, btnSmall, inputCls } from '@/lib/ui';

export const Route = createFileRoute('/app/booking/bookings/$id')({
  /*
    Which view this was opened from, carried so the link back returns to it.

    Opening a booking from Tuesday's grid and being put back on the Upcoming
    list is a small thing that happens every single time, and the browser's own
    back button is not an answer: half the ways into this page are a click on
    the list that comes after another click.
  */
  validateSearch: (s: Record<string, unknown>): { view?: string; date?: string } => ({
    ...(typeof s.view === 'string' ? { view: s.view } : {}),
    ...(typeof s.date === 'string' ? { date: s.date } : {}),
  }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(hostBookingQuery(params.id)),
  component: BookingDetailPage,
});

/**
 * A button that asks once before doing something that cannot be taken back.
 *
 * Every action on this screen that reaches the customer or cannot be undone —
 * resending their confirmation, re-creating the calendar entry that invites
 * them, recording a no-show, refunding — was a single click, often sitting in a
 * row beside harmless ones. The question appears in place rather than as a
 * browser dialog, which keeps it on the page and works the same on a phone.
 */
/**
 * Who is taking this appointment, and the owner's way to change it.
 *
 * The change has to exist because removing a member is refused while they still
 * have appointments: without it, somebody leaving meant cancelling real
 * customers to tidy up after them.
 */
function TakenBy({
  booking,
  busy,
  onChange,
}: {
  booking: HostBooking;
  busy?: boolean;
  onChange: () => void;
}) {
  const { t } = useTranslation(['booking', 'common']);
  const gone = booking.agent && !booking.agent.inWorkspace;

  return (
    <div>
      <span className="me-2">
        {booking.agent
          ? booking.agent.name || t('booking:bookings.agentGone')
          : t('booking:eventTypes.fields.takenByMe')}
      </span>
      {/* The appointment nobody is going to keep. Said plainly, because it
          otherwise reads as an ordinary booking. */}
      {gone ? (
        <span className="block text-xs text-amber-700">{t('booking:bookings.agentLeft')}</span>
      ) : null}
      {booking.canManage && !busy ? (
        <button type="button" onClick={onChange} className={btnSmall}>
          {t('booking:bookings.changeAgent')}
        </button>
      ) : null}
    </div>
  );
}

function ConfirmButton({
  label,
  question,
  onConfirm,
  disabled,
  className,
}: {
  label: string;
  question: string;
  onConfirm: () => void;
  disabled?: boolean;
  className: string;
}) {
  const { t } = useTranslation(['common']);
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAsking(true)}
        className={className}
      >
        {label}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className="text-neutral-600">{question}</span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setAsking(false);
          onConfirm();
        }}
        className={className}
      >
        {t('common:actions.confirm')}
      </button>
      <button type="button" onClick={() => setAsking(false)} className="underline">
        {t('common:actions.cancel')}
      </button>
    </span>
  );
}

function BookingDetailPage() {
  const { id } = Route.useParams();
  // Exactly what the bookings page understands, and nothing when it was opened
  // from somewhere with no view of its own.
  const { view, date } = Route.useSearch();
  const back = { ...(view ? { view } : {}), ...(date ? { date } : {}) } as {
    view?: 'upcoming' | 'past' | 'cancelled' | 'day' | 'week' | 'month';
    date?: string;
  };
  const { t, i18n } = useTranslation(['booking', 'common', 'errors']);
  const { data: user } = useCurrentUser();
  const { data: b } = useQuery({ ...hostBookingQuery(id), initialData: Route.useLoaderData() });
  /*
    The addresses the business copies on its own booking mail. Owner-scoped on
    the API, so a member looking at an appointment in somebody else's workspace
    simply gets none — which is right: they are not that business's back office.
  */
  const { data: settings } = useQuery({ ...bookingSettingsQuery, retry: false });
  const cc = settings?.ccEmails ?? [];
  const { data: members = [] } = useMembers();
  const reassign = useReassignBooking();
  const reassignError = reassign.error instanceof ApiRequestError ? reassign.error : null;
  // Everybody who could take it instead: the owner, and the members who have
  // joined. Whoever has it already is not an option.
  const agentOptions = [
    ...(user ? [{ id: user.id, label: t('booking:eventTypes.fields.takenByMe') }] : []),
    ...members
      .filter((m) => m.status === 'active' && m.agentId)
      .map((m) => ({ id: m.agentId as string, label: m.name ?? m.email })),
  ].filter((o) => o.id !== b.agent?.id);
  const locale = i18n.language as Locale;
  const zone = user?.timezone ?? b.hostTimezone;
  const cancel = useCancelHostBooking();
  const [refundChoice, setRefundChoice] = useState<'full' | 'none'>('full');
  /*
    The time picked, before the booking moves. Choosing used to move it on the
    tap, which here means the customer is emailed a new time for a slot the host
    may have hit by accident.
  */
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  /** What is still refundable, for both the question and the confirmation. */
  const remainingToRefund = formatMoney(
    {
      amount: (b.payment?.amount ?? 0) - (b.payment?.refundedAmount ?? 0),
      currency: b.payment?.currency ?? 'JPY',
    },
    locale,
  );
  /** What they actually paid, less anything already given back. */
  const paidAmount = b.payment
    ? formatMoney(
        { amount: b.payment.amount - b.payment.refundedAmount, currency: b.payment.currency },
        locale,
      )
    : '';
  const reschedule = useRescheduleHostBooking();
  const noShow = useMarkNoShow();
  const unmarkNoShow = useUnmarkNoShow();
  const resend = useResendConfirmation();
  const recreate = useRecreateCalendarEvent();
  const notes = useSetHostNotes();
  const refund = useRefundBooking();
  const [mode, setMode] = useState<'view' | 'cancel' | 'reschedule' | 'resend' | 'reassign'>(
    'view',
  );
  /*
    Who has been picked, before anybody acts on it.

    Choosing from the dropdown used to hand the appointment over on the spot —
    no save, no way back, and out of step with every other action on this page.
  */
  const [pickedAgent, setPickedAgent] = useState('');
  const { data: workspaceMembers = [] } = useMembers();
  const activeMembers = workspaceMembers.filter((m) => m.status === 'active');
  const [message, setMessage] = useState('');
  const [hostNotes, setHostNotes] = useState(b.hostNotes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const onError = (err: unknown) => setError(err instanceof ApiRequestError ? err.code : 'generic');
  const fmt = (iso: string, z: string) => {
    const d = DateTime.fromISO(iso, { zone: z }).setLocale(locale);
    return `${d.toFormat('DDDD')} ${d.toLocaleString(DateTime.TIME_SIMPLE)} (${d.offsetNameShort})`;
  };
  const isPast = new Date(b.end) < new Date();
  const active = b.status === 'confirmed' || b.status === 'awaiting_payment';

  return (
    <div className="max-w-2xl space-y-6">
      {/*
        A button, and it says where it goes.

        This used to be faint grey text with a chevron, at the top of a page
        whose actions are all proper buttons — so the eye went to the row of
        them instead, where the first thing that reads like "get out of here"
        is Cancel booking. Naming the destination also tells somebody who
        arrived from Thursday's grid that they will land back on it.
      */}
      <Link
        to="/app/booking/bookings"
        search={back}
        className={`${btnSecondary} inline-flex items-center gap-1.5`}
      >
        <span aria-hidden>←</span>
        {back.view
          ? t('booking:bookings.backTo', { view: t(`booking:bookings.views.${back.view}`) })
          : t('booking:bookings.backToList')}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: b.eventTypeColour ?? '#a3a3a3' }}
              aria-hidden
            />
            <span className="text-sm text-neutral-500">{pickLocale(b.eventTypeName, locale)}</span>
          </div>
          <h1 className="mt-1 text-xl font-semibold">{b.bookerName}</h1>
          <p className="text-sm text-neutral-600">
            <a href={`mailto:${b.booker.email}`} className="underline">
              {b.booker.email}
            </a>
            {b.booker.phone ? ` · ${b.booker.phone}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge
            label={t(`booking:bookings.status.${b.status}`)}
            tone={
              b.status === 'confirmed'
                ? 'green'
                : b.status === 'awaiting_payment'
                  ? 'amber'
                  : 'grey'
            }
          />
          {b.status === 'confirmed' ? (
            <Badge
              label={t(`booking:bookings.sync.${b.syncStatus}`)}
              tone={
                b.syncStatus === 'synced'
                  ? 'green'
                  : b.syncStatus === 'pending' || b.syncStatus === 'none'
                    ? 'grey'
                    : 'red'
              }
            />
          ) : null}
          {b.payment ? (
            <Badge
              label={t(`booking:bookings.payment.${b.payment.status}`)}
              tone={b.payment.status === 'paid' ? 'green' : 'amber'}
            />
          ) : null}
        </div>
      </header>

      {flash ? (
        <p className="rounded bg-green-50 px-3 py-2 text-sm text-green-800">{flash}</p>
      ) : null}
      {b.bookerEmailFailed ? (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          <p className="font-medium">{t('booking:bookings.emailFailed.title')}</p>
          <p className="mt-1">
            {t(`booking:bookings.emailFailed.${b.bookerEmailFailed.kind}`, {
              email: b.booker.email,
            })}
          </p>
          {b.bookerEmailFailed.diagnostic ? (
            <p className="mt-1 text-xs opacity-80">{b.bookerEmailFailed.diagnostic}</p>
          ) : null}
        </div>
      ) : null}
      {b.syncStatus === 'failed' || b.syncStatus === 'missing_externally' ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {t(`booking:bookings.syncHelp.${b.syncStatus}`)}
          {b.syncError ? <span className="block text-xs opacity-80">{b.syncError}</span> : null}
          <div className="mt-1">
            <ConfirmButton
              label={t('booking:bookings.recreateEvent')}
              question={t('booking:bookings.confirmRecreate')}
              onConfirm={() =>
                recreate.mutate(
                  { id },
                  { onSuccess: () => setFlash(t('booking:bookings.recreateQueued')), onError },
                )
              }
              className="font-medium underline"
            />
          </div>
        </div>
      ) : null}

      {/*
        Everything about the booking, while nothing is being asked.

        Rescheduling in particular needs the room — it is a calendar — and
        leaving the details, the notes and a row of other actions underneath it
        invites somebody to answer two questions at once.
      */}
      {mode === 'view' ? (
        <>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[140px_1fr]">
            {/*
          First, and only when somebody other than the account owner is taking
          it: on a page full of times and money, whose appointment this is comes
          before any of it. Absent on a solo account, where it would say the
          owner's own name on every booking.
        */}
            {b.agent || (b.canManage && activeMembers.length > 0) ? (
              <>
                <dt className={dt}>{t('booking:bookings.fields.takenBy')}</dt>
                <dd>
                  {/*
                Not while another question is open. Handing the appointment to
                somebody else in the middle of cancelling or rescheduling it is
                two decisions at once, and the second would act on the first's
                answer.
              */}
                  <TakenBy
                    booking={b}
                    busy={mode !== 'view' || resend.isPending}
                    onChange={() => {
                      setPickedAgent('');
                      setMode('reassign');
                    }}
                  />
                </dd>
              </>
            ) : null}
            <dt className={dt}>{t('booking:bookings.fields.when')}</dt>
            <dd>
              {fmt(b.start, zone)}
              {b.booker.timezone !== zone ? (
                <span className="block text-xs text-neutral-500">
                  {t('booking:bookings.fields.bookerTime')}: {fmt(b.start, b.booker.timezone)}
                </span>
              ) : null}
            </dd>
            <dt className={dt}>{t('booking:bookings.fields.location')}</dt>
            <dd>
              {t(`booking:eventTypes.location.${b.location.kind}`)}
              {b.location.kind === 'in_person' ? ` · ${b.location.address}` : ''}
              {b.joinUrl ? (
                <a href={b.joinUrl} target="_blank" rel="noreferrer" className="ml-2 underline">
                  {t('booking:public.join')}
                </a>
              ) : null}
            </dd>
            {b.answers.map((a) => (
              <AnswerRow key={a.questionId} label={pickLocale(a.label, locale)} value={a.value} />
            ))}
            {b.bookerNotes ? (
              <>
                <dt className={dt}>{t('booking:bookings.fields.bookerNotes')}</dt>
                <dd className="whitespace-pre-line">{b.bookerNotes}</dd>
              </>
            ) : null}
            {b.payment ? (
              <>
                <dt className={dt}>{t('booking:bookings.fields.payment')}</dt>
                <dd>
                  {formatMoney({ amount: b.payment.amount, currency: b.payment.currency }, locale)}
                  {b.payment.refundedAmount > 0
                    ? ` · ${t('booking:public.payment.refundedAmount', { amount: formatMoney({ amount: b.payment.refundedAmount, currency: b.payment.currency }, locale) })}`
                    : ''}
                  {b.payment.receiptUrl ? (
                    <a href={b.payment.receiptUrl} className="ml-2 underline">
                      {t('booking:public.payment.receipt')}
                    </a>
                  ) : null}
                </dd>
              </>
            ) : null}
            {b.cancellation ? (
              <>
                <dt className={dt}>{t('booking:bookings.fields.cancelled')}</dt>
                <dd>
                  {t(`booking:bookings.cancelledBy.${b.cancellation.by}`)} ·{' '}
                  {fmt(b.cancellation.at, zone)}
                  {b.cancellation.reason ? (
                    <span className="block text-neutral-600">“{b.cancellation.reason}”</span>
                  ) : null}
                </dd>
              </>
            ) : null}
            <dt className={dt}>{t('booking:bookings.fields.source')}</dt>
            <dd>
              {t(`booking:bookings.source.${b.source}`)} · {fmt(b.createdAt, zone)}
            </dd>
          </dl>

          {/*
        Private notes belong to the business, and saving them is owner-scoped on
        the API. A member viewing their own appointment in somebody else's
        workspace sees the booking, not the back office.
      */}
          {b.canManage ? (
            <section className="rounded-lg border border-neutral-200 bg-white p-3 sm:p-4">
              <label className="block">
                <span className="mb-1 block text-sm font-medium">
                  {t('booking:bookings.fields.hostNotes')}
                </span>
                <textarea
                  rows={3}
                  value={hostNotes}
                  onChange={(e) => setHostNotes(e.target.value)}
                  className="w-full rounded border border-neutral-300 px-3 py-2 text-sm"
                />
              </label>
              <button
                type="button"
                disabled={hostNotes === (b.hostNotes ?? '') || notes.isPending}
                onClick={() => notes.mutate({ id, hostNotes }, { onError })}
                className={`${btnSecondary} mt-2`}
              >
                {t('common:actions.save')}
              </button>
            </section>
          ) : null}

          {/*
        The things that act on the appointment, kept apart from the notes.

        They shared a stretch of screen with the private notes and their Save
        button, so "save what I typed" and "call this off" were two buttons in
        the same place doing very different things.
      */}
          {b.canManage && (active || (b.status === 'completed' && isPast)) ? (
            <div className="grid grid-cols-2 gap-2 border-neutral-200 border-t pt-4 sm:flex sm:flex-wrap sm:items-center">
              {b.status === 'confirmed' && !isPast ? (
                <button
                  type="button"
                  onClick={() => setMode('reschedule')}
                  className={btnSecondary}
                >
                  {t('booking:public.reschedule')}
                </button>
              ) : null}
              {b.status === 'confirmed' ? (
                <button type="button" onClick={() => setMode('resend')} className={btnSecondary}>
                  {t('booking:bookings.resend')}
                </button>
              ) : null}
              {b.payment &&
              (b.payment.status === 'paid' || b.payment.status === 'partially_refunded') &&
              b.payment.amount - b.payment.refundedAmount > 0 ? (
                /* Was a window.confirm: a browser dialog on a page that asks its
               own questions everywhere else, and one some mobile browsers
               suppress outright. Same component as the rest now. */
                <ConfirmButton
                  label={t('booking:payments.refund')}
                  question={t('booking:bookings.confirmRefund', { amount: remainingToRefund })}
                  disabled={refund.isPending}
                  onConfirm={() =>
                    refund.mutate(
                      { id },
                      {
                        onSuccess: () =>
                          setFlash(
                            t('booking:payments.refundQueued', { amount: remainingToRefund }),
                          ),
                        onError,
                      },
                    )
                  }
                  className={btnSecondary}
                />
              ) : null}
              {(b.status === 'confirmed' || b.status === 'completed') && isPast ? (
                <ConfirmButton
                  label={t('booking:bookings.markNoShow')}
                  question={t('booking:bookings.confirmNoShow')}
                  onConfirm={() => noShow.mutate({ id }, { onError })}
                  className={btnSecondary}
                />
              ) : null}
              {/* No confirmation: undoing is itself the safe direction, and it can
              simply be marked again. */}
              {b.status === 'no_show' ? (
                <button
                  type="button"
                  onClick={() => unmarkNoShow.mutate({ id }, { onError })}
                  disabled={unmarkNoShow.isPending}
                  className={btnSecondary}
                >
                  {t('booking:bookings.unmarkNoShow')}
                </button>
              ) : null}

              {/*
            Last, and set apart.

            It used to sit second in this row, beside Reschedule, which is
            where somebody looking for the way out of the page lands first.
            Calling off a customer's appointment should take a deliberate
            reach, not an idle one.
          */}
              {active ? (
                <button
                  type="button"
                  onClick={() => setMode('cancel')}
                  className={`${btnSecondary} ms-auto border-red-300 text-red-700`}
                >
                  {t('booking:public.cancel')}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {/*
        Sending mail to a customer again, with who gets what spelled out.

        It used to be a one-line "are you sure", which is the wrong question:
        nobody is unsure whether they pressed the button, they are unsure what
        it does. Three different people can hear about this, and one of them is
        the customer.
      */}
      {/*
        Handing the appointment to somebody else.

        It says what moves with it, because the customer hears about this and
        two calendars change.
      */}
      {mode === 'reassign' ? (
        <section className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3 sm:p-4">
          <h2 className="text-sm font-medium">{t('booking:bookings.reassignTitle')}</h2>
          <p className="text-sm text-neutral-600">
            {t('booking:bookings.reassignCurrent', {
              name: b.agent?.name || t('booking:eventTypes.fields.takenByMe'),
            })}
          </p>
          <label className="block text-sm">
            <span className="mb-1 block text-neutral-600">{t('booking:bookings.chooseAgent')}</span>
            <select
              value={pickedAgent}
              onChange={(e) => setPickedAgent(e.target.value)}
              className={inputCls}
            >
              <option value="">{t('booking:bookings.reassignPick')}</option>
              {agentOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {pickedAgent ? (
            <ul className="list-disc space-y-1 ps-5 text-xs text-neutral-600">
              <li>{t('booking:bookings.reassignEmail')}</li>
              <li>{t('booking:bookings.reassignCalendars')}</li>
              <li>{t('booking:bookings.reassignBusy')}</li>
            </ul>
          ) : null}
          {reassignError ? (
            <p className="text-sm text-red-600">{t(`errors:${reassignError.code}`)}</p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!pickedAgent || reassign.isPending}
              onClick={() =>
                reassign.mutate(
                  { id, agentId: pickedAgent },
                  {
                    onSuccess: () => {
                      setMode('view');
                      setFlash(t('booking:bookings.reassigned'));
                    },
                  },
                )
              }
              className={btnPrimary}
            >
              {t('common:actions.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                reassign.reset();
                setMode('view');
              }}
              className={btnSecondary}
            >
              {t('common:actions.back')}
            </button>
          </div>
        </section>
      ) : null}

      {mode === 'resend' ? (
        <section className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3 sm:p-4">
          <h2 className="text-sm font-medium">{t('booking:bookings.resendTitle')}</h2>
          <ul className="space-y-2 text-sm">
            <li>
              <span className="font-medium">{b.bookerName}</span>{' '}
              <span className="text-neutral-500">{b.booker.email}</span>
              <span className="block text-xs text-neutral-600">
                {t('booking:bookings.resendBooker', {
                  event: pickLocale(b.eventTypeName, locale),
                  when: fmt(b.start, zone),
                })}
              </span>
            </li>
            <li>
              <span className="font-medium">
                {b.agent?.name || t('booking:eventTypes.fields.takenByMe')}
              </span>
              <span className="block text-xs text-neutral-600">
                {t('booking:bookings.resendHost')}
              </span>
            </li>
            {cc.length > 0 ? (
              <li>
                <span className="font-medium">{cc.join(', ')}</span>
                <span className="block text-xs text-neutral-600">
                  {t('booking:bookings.resendCc')}
                </span>
              </li>
            ) : null}
          </ul>
          <p className="text-xs text-neutral-500">{t('booking:bookings.resendIntegrations')}</p>
          {error ? <p className="text-sm text-red-600">{t(`errors:${error}`)}</p> : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={resend.isPending}
              onClick={() =>
                resend.mutate(
                  { id },
                  {
                    onSuccess: () => {
                      setMode('view');
                      setFlash(t('booking:bookings.resent'));
                    },
                    onError,
                  },
                )
              }
              className={btnPrimary}
            >
              {t('booking:bookings.resendConfirm')}
            </button>
            <button type="button" onClick={() => setMode('view')} className={btnSecondary}>
              {t('common:actions.back')}
            </button>
          </div>
        </section>
      ) : null}

      {mode === 'cancel' ? (
        <section className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3 sm:p-4">
          <h2 className="text-sm font-medium">{t('booking:bookings.cancelTitle')}</h2>
          {/*
            The money is named and the choice is the host's. It used to say the
            booker would be refunded in full and do exactly that, whatever the
            event type's refund policy said — a refund beyond the policy, taken
            from the host's own Stripe balance, decided for them.

            Full stays the default: a customer whose appointment is called off
            by the person who was going to give it has done nothing to deserve
            losing their money.
          */}
          {b.payment?.status === 'paid' ? (
            <div className="space-y-2 text-sm">
              <p className="text-neutral-600">
                {t('booking:bookings.hostCancelRefund', { amount: paidAmount })}
              </p>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="host-refund"
                  checked={refundChoice === 'full'}
                  onChange={() => setRefundChoice('full')}
                  className="mt-0.5 size-4"
                />
                <span>{t('booking:bookings.hostRefundFull', { amount: paidAmount })}</span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="host-refund"
                  checked={refundChoice === 'none'}
                  onChange={() => setRefundChoice('none')}
                  className="mt-0.5 size-4"
                />
                <span>
                  {t('booking:bookings.hostRefundNone')}
                  <span className="mt-0.5 block text-xs text-neutral-500">
                    {t('booking:bookings.hostRefundNoneHelp')}
                  </span>
                </span>
              </label>
            </div>
          ) : null}
          <textarea
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('booking:bookings.cancelMessage')}
            className="w-full rounded border border-neutral-300 px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={cancel.isPending}
              onClick={() =>
                cancel.mutate(
                  { id, ...(message ? { message } : {}), refund: refundChoice },
                  { onSuccess: () => setMode('view'), onError },
                )
              }
              className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
            >
              {t('booking:public.confirmCancel')}
            </button>
            <button type="button" onClick={() => setMode('view')} className={btnSecondary}>
              {t('common:actions.back')}
            </button>
          </div>
        </section>
      ) : null}

      {mode === 'reschedule' && user?.username ? (
        <section className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3 sm:p-4">
          <h2 className="text-sm font-medium">
            {pendingStart
              ? t('booking:public.confirmMoveTitle')
              : t('booking:public.rescheduleTitle')}
          </h2>
          {pendingStart ? (
            <div className="space-y-3 text-sm">
              <p>
                <span className="text-neutral-500">{t('booking:public.confirmMoveTo')}: </span>
                <span className="font-medium">
                  {DateTime.fromISO(pendingStart, { zone }).setLocale(locale).toFormat('DDDD · t')}
                </span>
              </p>
              <p className="text-xs text-neutral-500">{t('booking:bookings.confirmMoveNote')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={reschedule.isPending}
                  onClick={() =>
                    reschedule.mutate(
                      { id, start: pendingStart },
                      {
                        onSuccess: () => {
                          setPendingStart(null);
                          setMode('view');
                          setFlash(t('booking:bookings.rescheduled'));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        },
                        onError,
                      },
                    )
                  }
                  className="min-h-11 rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40 sm:min-h-0 sm:py-1.5"
                >
                  {t('booking:public.confirmMove')}
                </button>
                <button
                  type="button"
                  onClick={() => setPendingStart(null)}
                  className={btnSecondary}
                >
                  {t('booking:public.pickAnother')}
                </button>
              </div>
            </div>
          ) : null}
          {/*
            Hidden rather than unmounted: taking it out of the tree loses the
            day the picker has open, so "choose a different time" would drop
            you back to a bare calendar you had already navigated.
          */}
          <div className={pendingStart ? 'hidden' : ''}>
            <SlotPicker
              username={user.username}
              slug={b.eventTypeSlug}
              locale={locale}
              timezone={zone}
              onTimezoneChange={() => undefined}
              maxDaysAhead={365}
              onSelect={(iso) => setPendingStart(iso)}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setPendingStart(null);
              setMode('view');
            }}
            className={btnSecondary}
          >
            {t('common:actions.back')}
          </button>
        </section>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-red-600">
          {t(`errors:${error}`)}
        </p>
      ) : null}
    </div>
  );
}

function AnswerRow({ label, value }: { label: string; value: string | boolean }) {
  const { t } = useTranslation('common');
  return (
    <>
      <dt className={dt}>{label}</dt>
      <dd className="whitespace-pre-line">
        {typeof value === 'boolean' ? (value ? t('actions.confirm') : '—') : value}
      </dd>
    </>
  );
}

function Badge({ label, tone }: { label: string; tone: 'green' | 'amber' | 'red' | 'grey' }) {
  const cls = {
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
    grey: 'bg-neutral-200 text-neutral-700',
  }[tone];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

const dt = 'text-neutral-500';
